import { useState, useEffect, useCallback } from "react";
import {
    Send, Loader2, RefreshCw, Link2, AlertTriangle, CheckCircle, FlaskConical,
    Trash2, Info,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import {
    META_CONVERSION_CONFIG_URL, META_CONVERSION_SEND_URL, META_CONVERSION_LOGS_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

const STATUS_TONE = {
    sent:    "text-emerald-600 dark:text-emerald-400",
    skipped: "text-muted-foreground",
    failed:  "text-red-600 dark:text-red-400",
    pending: "text-blue-600 dark:text-blue-400",
};

const Section = ({ icon: Icon, title, subtitle, children, action }) => (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <Icon className="h-4 w-4" />
                </div>
                <div>
                    <h2 className="text-sm font-semibold text-foreground">{title}</h2>
                    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {action}
        </div>
        {children}
    </div>
);

export default function MetaConversions() {
    const { fetch, create, update, remove } = useApiMethods();

    const [configs, setConfigs] = useState([]);
    const [stages, setStages] = useState([]);
    const [events, setEvents] = useState([]);
    const [logs, setLogs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");

    const [pixelId, setPixelId] = useState("");
    const [token, setToken] = useState("");
    const [testCode, setTestCode] = useState("");

    const config = configs[0] || null;
    const [draft, setDraft] = useState([]);

    const load = useCallback(() => {
        setLoading(true);
        fetch(META_CONVERSION_CONFIG_URL)
            .then((res) => {
                if (res?.status === "success") {
                    setConfigs(res.data || []);
                    setStages(res.stages || []);
                    setEvents(res.suggested_events || []);
                    setDraft((res.data?.[0]?.mappings) || []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load conversion settings");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load conversion settings"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadLogs = useCallback(() => {
        fetch(`${META_CONVERSION_LOGS_URL}?limit=50`)
            .then((res) => { if (res?.status === "success") setLogs(res); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); loadLogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const connect = () => {
        if (!pixelId.trim() || !token.trim()) {
            return ToastMsg("error", "Pixel ID and access token are both required");
        }
        setBusy("connect");
        create(META_CONVERSION_CONFIG_URL, {
            pixel_id: pixelId.trim(), access_token: token.trim(),
            test_event_code: testCode.trim(),
        })
            .then((res) => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message);
                    setToken("");
                    load();
                } else { ToastMsg("error", res?.message || "Could not connect"); }
            })
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Could not connect"))
            .finally(() => setBusy(""));
    };

    const save = (patch) => {
        setBusy("save");
        update(META_CONVERSION_CONFIG_URL, { id: config.id, ...patch })
            .then((res) => {
                if (res?.status === "success") { ToastMsg("success", "Saved"); load(); }
                else ToastMsg("error", res?.message || "Save failed");
            })
            .catch(() => ToastMsg("error", "Save failed"))
            .finally(() => setBusy(""));
    };

    const run = (dryRun) => {
        setBusy(dryRun ? "dry" : "send");
        create(META_CONVERSION_SEND_URL, { id: config.id, dry_run: dryRun, limit: 500 })
            .then((res) => {
                if (res?.status === "success") {
                    ToastMsg(res.result?.failed ? "error" : "success", res.message);
                    loadLogs();
                    load();
                } else ToastMsg("error", res?.message || "Run failed");
            })
            .catch(() => ToastMsg("error", "Run failed"))
            .finally(() => setBusy(""));
    };

    const setRow = (i, key, value) =>
        setDraft((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
    const addRow = () =>
        setDraft((prev) => [...prev, { lead_stage: "", event_name: "Lead", send_value: false, is_enabled: true }]);
    const dropRow = (i) => setDraft((prev) => prev.filter((_, idx) => idx !== i));

    const match = logs?.match;

    return (
        <div className="space-y-5">
            {!config && (
                <Section icon={Link2} title="Send outcomes back to Meta"
                    subtitle="Meta only knows a form was submitted. Tell it which leads qualified, booked a demo or signed, and it can optimise toward buyers instead of form-fillers.">
                    <div className="flex flex-col gap-2">
                        <Input placeholder="Pixel / dataset ID" value={pixelId}
                            onChange={(e) => setPixelId(e.target.value)} className="text-sm" />
                        <Input type="password" placeholder="Access token — must include ads_management"
                            value={token} onChange={(e) => setToken(e.target.value)}
                            className="font-mono text-xs" />
                        <Input placeholder="Test Event Code (optional, from Events Manager)"
                            value={testCode} onChange={(e) => setTestCode(e.target.value)}
                            className="text-sm" />
                        <Button onClick={connect} disabled={busy === "connect"} className="self-start">
                            {busy === "connect" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <Link2 className="h-4 w-4 mr-2" />}
                            Connect pixel
                        </Button>
                        <p className="text-[11px] text-muted-foreground">
                            The reporting token will not work here — reading spend needs
                            <code className="mx-1 font-mono">ads_read</code>, sending conversions is a write
                            and needs <code className="mx-1 font-mono">ads_management</code>.
                        </p>
                    </div>
                </Section>
            )}

            {config && (
                <>
                    <Section icon={Send} title={`Pixel ${config.pixel_name || config.pixel_id}`}
                        subtitle={`Token ${config.token_preview || "—"} · last sent ${config.last_sent_at || "never"}`}
                        action={
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => run(true)}
                                    disabled={!!busy}>
                                    {busy === "dry" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                        : <FlaskConical className="h-3.5 w-3.5 mr-1.5" />}
                                    Dry run
                                </Button>
                                <Button size="sm" onClick={() => run(false)} disabled={!!busy}>
                                    {busy === "send" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                        : <Send className="h-3.5 w-3.5 mr-1.5" />}
                                    Send now
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600"
                                    onClick={() => remove(META_CONVERSION_CONFIG_URL, { id: config.id })
                                        .then(() => { ToastMsg("success", "Disconnected"); load(); })}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        }>
                        <div className="flex flex-wrap items-center gap-4">
                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                Test event code
                                <Input value={config.test_event_code || ""}
                                    onChange={(e) => setConfigs((c) =>
                                        [{ ...c[0], test_event_code: e.target.value }, ...c.slice(1)])}
                                    onBlur={(e) => save({ test_event_code: e.target.value })}
                                    placeholder="none — sending live"
                                    className="h-8 w-40 text-xs" />
                            </label>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                Send outcomes from the last
                                <select value={Math.min(config.max_event_age_days || 7, 7)}
                                    onChange={(e) => save({ max_event_age_days: e.target.value })}
                                    className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground">
                                    {[1, 2, 3, 4, 5, 6, 7].map((d) =>
                                        <option key={d} value={d}>{d} day{d > 1 ? "s" : ""}</option>)}
                                </select>
                            </label>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border p-3">
                            <label className="flex items-center gap-2 text-xs font-medium text-foreground cursor-pointer">
                                <input type="checkbox" checked={!!config.auto_send}
                                    disabled={busy === "save"}
                                    onChange={(e) => save({ auto_send: e.target.checked })}
                                    className="rounded border-input" />
                                Send automatically
                            </label>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                every
                                <select value={config.auto_send_interval_minutes || 15}
                                    disabled={!config.auto_send}
                                    onChange={(e) => save({ auto_send_interval_minutes: e.target.value })}
                                    className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground disabled:opacity-50">
                                    {[[15, "15 minutes"], [30, "30 minutes"], [60, "hour"],
                                      [180, "3 hours"], [360, "6 hours"], [720, "12 hours"],
                                      [1440, "day"]].map(([m, label]) =>
                                        <option key={m} value={m}>{label}</option>)}
                                </select>
                            </label>
                            <span className="text-[11px] text-muted-foreground">
                                {config.auto_send
                                    ? `Last automatic run ${config.last_auto_run_at || "pending — within 15 minutes"}`
                                    : "Off — outcomes go only when someone presses Send now."}
                            </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground -mt-2">
                            Meta refuses events more than 7 days old, so a lead must be sent within a
                            week of reaching its stage.
                        </p>

                        {config.test_event_code ? (
                            <div className="flex items-start gap-2 rounded-lg p-2.5 text-[11px] bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300">
                                <FlaskConical className="h-3.5 w-3.5 mt-px shrink-0" />
                                <span>
                                    Test mode. Events appear in Events Manager → Test Events and are
                                    excluded from optimisation and reporting. Clear the code to send live.
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-start gap-2 rounded-lg p-2.5 text-[11px] bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                                <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                                <span>Live mode — events count toward optimisation and reporting.</span>
                            </div>
                        )}

                        {config.last_error && (
                            <div className="flex items-start gap-2 rounded-md p-2 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span className="break-all">{config.last_error}</span>
                            </div>
                        )}
                    </Section>

                    <Section icon={CheckCircle} title="Which outcome becomes which Meta event"
                        subtitle="The event you send is the event Meta can optimise toward — this mapping is the most consequential setting here."
                        action={
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={addRow}>Add stage</Button>
                                <Button size="sm" onClick={() => save({ mappings: draft })}
                                    disabled={busy === "save"}>
                                    {busy === "save" && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                                    Save mapping
                                </Button>
                            </div>
                        }>
                        <div className="space-y-2">
                            {draft.map((row, i) => (
                                <div key={i} className="flex flex-wrap items-center gap-2">
                                    <select value={row.lead_stage}
                                        onChange={(e) => setRow(i, "lead_stage", e.target.value)}
                                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground min-w-[150px]">
                                        <option value="">Select stage…</option>
                                        {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <span className="text-muted-foreground text-xs">→</span>
                                    <select value={row.event_name}
                                        onChange={(e) => setRow(i, "event_name", e.target.value)}
                                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground min-w-[150px]">
                                        {events.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                                    </select>
                                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                                        <input type="checkbox" checked={!!row.send_value}
                                            onChange={(e) => setRow(i, "send_value", e.target.checked)}
                                            className="rounded border-input" />
                                        send deal value
                                    </label>
                                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                                        <input type="checkbox" checked={row.is_enabled !== false}
                                            onChange={(e) => setRow(i, "is_enabled", e.target.checked)}
                                            className="rounded border-input" />
                                        on
                                    </label>
                                    <button onClick={() => dropRow(i)}
                                        className="text-muted-foreground hover:text-red-600 p-1">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                            {draft.length === 0 && (
                                <p className="text-xs text-muted-foreground">
                                    No stages mapped — nothing will be sent.
                                </p>
                            )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            Sending a value with a won deal is what unlocks value-based bidding and real
                            ROAS in Ads Manager.
                        </p>
                    </Section>

                    <Section icon={RefreshCw} title="What has been sent"
                        subtitle="Also shows how Meta identified each person, which is how a poor match rate gets diagnosed."
                        action={
                            <Button size="sm" variant="outline" onClick={loadLogs}>
                                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
                            </Button>
                        }>
                        {match && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[["Sent", match.sent], ["Matched by lead id", match.by_lead_id],
                                  ["Matched by phone", match.by_phone], ["Matched by email", match.by_email]].map(
                                    ([label, n]) => (
                                        <div key={label} className="rounded-lg border border-border p-3">
                                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
                                            <p className="text-lg font-bold text-foreground tabular-nums">{n ?? 0}</p>
                                        </div>
                                    ))}
                            </div>
                        )}

                        {logs?.summary && Object.keys(logs.summary).length > 0 && (
                            <div className="flex flex-wrap gap-3 text-xs">
                                {Object.entries(logs.summary).map(([k, v]) => (
                                    <span key={k} className={cn("font-medium", STATUS_TONE[k])}>
                                        {v} {k}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                    <tr className="text-muted-foreground">
                                        <th className="px-3 py-2 text-left font-medium">Lead</th>
                                        <th className="px-3 py-2 text-left font-medium">Stage → Event</th>
                                        <th className="px-3 py-2 text-left font-medium">Matched on</th>
                                        <th className="px-3 py-2 text-right font-medium">Value</th>
                                        <th className="px-3 py-2 text-left font-medium">When</th>
                                        <th className="px-3 py-2 text-left font-medium">Status</th>
                                        <th className="px-3 py-2 text-left font-medium">Sent by</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {(logs?.data || []).map((row) => (
                                        <tr key={row.id} className="hover:bg-accent/30">
                                            <td className="px-3 py-2 text-foreground truncate max-w-[160px]">
                                                {row.customer_name || `#${row.customer_id}`}
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                                {row.lead_stage} → <span className="text-foreground">{row.event_name}</span>
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                                {row.matched_on?.length ? row.matched_on.join(", ") : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                                                {row.value != null ? `₹${row.value.toLocaleString("en-IN")}` : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                                                {row.event_time}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={cn("font-medium", STATUS_TONE[row.status])}>
                                                    {row.status}{row.is_test && " (test)"}
                                                </span>
                                                {row.skip_reason && (
                                                    <p className="text-[10px] text-muted-foreground">{row.skip_reason}</p>
                                                )}
                                                {row.sent_at && (
                                                    <p className="text-[10px] text-muted-foreground whitespace-nowrap">{row.sent_at}</p>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {row.sent_by?.type === "system" ? (
                                                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                                                        {row.sent_by.label}
                                                    </span>
                                                ) : (
                                                    <span className="text-foreground">{row.sent_by?.label || "—"}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {!logs?.data?.length && (
                                        <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                                            Nothing sent yet. Try a dry run first — it builds every payload and
                                            reports the match rate without contacting Meta.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
                            <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                            <span>
                                Personal data is never sent. Email and phone are normalised and SHA-256
                                hashed; a lead carrying Meta's own leadgen_id needs no personal data at all.
                                Each outcome is sent once — a stable event id stops a retry counting a sale twice.
                            </span>
                        </div>
                    </Section>
                </>
            )}

            {loading && (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                </div>
            )}
        </div>
    );
}
